import React, { ReactNode, useEffect } from "react";

import sum from "hash-sum";
import { useDispatch } from "react-redux";
import { useDebouncedCallback } from "use-debounce";

// import { pollMyWork } from '@app/state/apis/preFetchApi'; << panormaic stuff
// Assuming EComponentStack is defined elsewhere
import { EComponentStack } from "@app/state/constants";
import { useFxDispatch } from "@app/statefx";

type ComponentStackContextType = {
  addComponent: (name: EComponentStack) => void;
  removeComponent: (name: EComponentStack) => void;
  components: EComponentStack[];
};

interface ComponentStackProviderProps {
  children: ReactNode;
}

const ComponentStackCtx = React.createContext<ComponentStackContextType>({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  addComponent: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  removeComponent: () => {},
  components: [],
});

export const ComponentStackProvider: React.FC<ComponentStackProviderProps> = ({
  children,
}) => {
  const [components, setComponents] = React.useState<EComponentStack[]>([]);

  const addComponent = (name: EComponentStack) => {
    setComponents((prevComponents) =>
      prevComponents.includes(name) ? prevComponents : [...prevComponents, name]
    );
  };

  const removeComponent = (name: EComponentStack) => {
    setComponents((prevComponents) =>
      prevComponents.filter((component) => component !== name)
    );
  };

  const contextValue: ComponentStackContextType = {
    addComponent,
    removeComponent,
    components,
  };

  return (
    <ComponentStackCtx.Provider value={contextValue}>
      {children}
    </ComponentStackCtx.Provider>
  );
};
/* -------------------------------------------------------------------------- */

export const useComponentStack = (thisComponent: EComponentStack) => {
  const fxDispatch = useFxDispatch();
  const sagaDispatch = useDispatch();
  const debouncedFxDispatch = useDebouncedCallback(
    (action) => fxDispatch(action),
    700
  );
  const debouncedSagaDispatch = useDebouncedCallback(
    (action) => sagaDispatch(action),
    700
  );

  const context = React.useContext(ComponentStackCtx);
  if (!context) {
    throw new Error(
      "useComponentStack must be used within a ComponentStackProvider"
    );
  }

  const stop = (component: EComponentStack) => {
    switch (component) {
      case EComponentStack.HomePage:
        debouncedSagaDispatch({ type: "POLLING_TEARDOWN" });
        break;
      case EComponentStack.InvoicingInKarma:
        debouncedFxDispatch({ type: "STOP_POLLING_IDB", payload: component });
        break;
      default:
        break;
    }
  };

  const start = (component: EComponentStack) => {
    switch (component) {
      case EComponentStack.HomePage:
        debouncedFxDispatch({ type: "STOP_POLLING_IDB", payload: component });
        // heavy. we should be lighter. like :poll_unsubscribe_{xxxx} << this works also and is fine grained
        debouncedSagaDispatch({ type: "POLLING_TEARDOWN" });
        // debouncedSagaDispatch(pollMyWork()); // PANORAMIC stuff
        break;
      case EComponentStack.InvoicingInKarma:
      case EComponentStack.InvoiceTracking:
        debouncedSagaDispatch({ type: "POLLING_TEARDOWN" });
        debouncedFxDispatch({ type: "STOP_POLLING_IDB", payload: component });
        debouncedFxDispatch({ type: "START_POLLING_IDB", payload: component });
        break;
      default:
        break;
    }
  };

  React.useEffect(() => {
    if (!context.components.includes(thisComponent)) {
      context.addComponent(thisComponent);
      start(thisComponent);
    }
    return () => {
      stop(thisComponent);
      context.removeComponent(thisComponent);
    };
  }, [thisComponent]);

  return {
    components: context.components,
  };
};
